import { inject, Injectable } from '@angular/core';
import { DomService, PlatformService } from '@big-direkt/utils/shared';
import { BehaviorSubject } from 'rxjs';
import { EnvironmentService } from './environment.service';
import { WindowService } from './window.service';

@Injectable({
    providedIn: 'root',
})
export class CoBrowsingService {
    private readonly domService = inject(DomService);
    private readonly environmentService = inject(EnvironmentService);
    private readonly platformService = inject(PlatformService);
    private readonly windowService = inject(WindowService);
    private readonly isCoBrowsingActive$ = new BehaviorSubject<boolean>(false);
    private coBrowsingUrl!: string | undefined;
    private isInitialized = false;
    private scriptTag: HTMLScriptElement | undefined;

    public init(): void {
        this.coBrowsingUrl = this.environmentService.coBrowsingUrl;

        if (!this.coBrowsingUrl || this.isInitialized || this.platformService.isPlatformServer()) {
            return;
        }

        this.scriptTag = this.domService.createScriptTag(this.coBrowsingUrl, {
            target: this.windowService.document().head,
            async: true,
        });

        this.isInitialized = true;

        if (!this.scriptTag) {
            return;
        }

        /**
         * If we have a running co browsing session, let's listen to start and stop event so we influence `isCoBrowsingActive$`-state
         */
        this.scriptTag.addEventListener('load', () => {
            const cv = this.windowService.nativeWindow()?.CV;

            if(!cv) {
                return;
            }

            const { cobrowse } = cv;

            if (typeof cobrowse.addCobrowsingStartedListener !== 'function' || typeof cobrowse.addCobrowsingStoppedListener !== 'function') {
                return;
            }

            this.windowService.nativeWindow()?.CV.cobrowse.addCobrowsingStartedListener(() => {
                this.isCoBrowsingActive$.next(true);
            });

            this.windowService.nativeWindow()?.CV.cobrowse.addCobrowsingStoppedListener(() => {
                this.isCoBrowsingActive$.next(false);
            });
        });
    }

    public startCoBrowsing(): void {
        this.isCoBrowsingActive$.next(true);

        /**
         * Case 1: co browsing script HAS NOT been injected
         * Starts the cobrowsing session after the script has been injected to the dom
         */
        if (this.scriptTag) {
            this.scriptTag.addEventListener('load', () => {
                this.windowService.nativeWindow()?.CV.cobrowse.start();
            });
        }

        /**
         * Case 2: co browsing script has been injected to the dom so we have access
         * Starts the cobrowsing session. See: https://docs.chatvisor.com/docs/jsapi01-cobrowsing/
         */
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (typeof this.windowService.nativeWindow()?.CV?.cobrowse?.start === 'function') {
            this.windowService.nativeWindow()?.CV.cobrowse.start();
        }
    }

    public stopCoBrowsing(): void {
        this.isCoBrowsingActive$.next(false);

        // Stops the cobrowsing session. See: https://docs.chatvisor.com/docs/jsapi01-cobrowsing/
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (typeof this.windowService.nativeWindow()?.CV?.cobrowse?.stop === 'function') {
            this.windowService.nativeWindow()?.CV.cobrowse.stop();
        }
    }

    public getCoBrowsingActiveState(): BehaviorSubject<boolean> {
        return this.isCoBrowsingActive$;
    }
}
